import type { ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight, Flag } from 'lucide-react'
import type { Dashboard, Live, Pair } from '@/lib/statsApi'
import { change, fmtHours, fmtInt, shortDay } from '@/lib/statsFormat'
import { Sparkline } from '@/components/stats/charts'
import { cx } from '@/lib/cx'
import styles from './StatsPage.module.css'

export function Card({ children }: { children: ReactNode }) {
  return <section className={styles.card}>{children}</section>
}

/** Плитка: значение за период и изменение к такому же периоду до него (стрелка и знак, не только цвет). */
export function Tile({ label, pair, prev, spark, format = fmtInt }: { label: string; pair: Pair; prev: string; spark?: number[]; format?: (n: number) => string }) {
  const c = change(pair)
  return (
    <div className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <div className={styles.tileRow}>
        <span className={styles.tileValue}>{format(pair.now)}</span>
        {spark ? <Sparkline values={spark} /> : null}
      </div>
      <span className={cx(styles.delta, c.dir === 'up' && styles.up, c.dir === 'down' && styles.down)}>
        {c.dir === 'up' ? <ArrowUpRight size={14} aria-hidden="true" /> : c.dir === 'down' ? <ArrowDownRight size={14} aria-hidden="true" /> : null}
        {c.dir === 'up' || c.dir === 'down' ? `${c.text} ${prev}` : c.text}
      </span>
    </div>
  )
}

/** Верх страницы: главная цифра, «сейчас» и итоги по сервису. */
export function Overview({ d, live }: { d: Dashboard; live: Live | undefined }) {
  const totals = live?.totals ?? d.totals
  const value = (n: number | undefined) => n === undefined ? '—' : fmtInt(n)
  return (
    <section className={styles.top}>
      <div className={styles.hero}>
        <span className={styles.heroLabel}>Активных сегодня</span>
        <span className={styles.heroValue}>{live ? fmtInt(live.active_today) : '—'}</span>
        <span className={styles.heroSub}>новых сегодня: {live ? fmtInt(live.new_today) : '—'}</span>
      </div>
      <div className={styles.side}>
        <div className={styles.nowHeader}>
          <h2>Сейчас</h2>
          <span>Сегодня по Москве</span>
        </div>
        <dl className={styles.nowGrid} aria-live="polite">
          <div>
            <dt><span className={styles.liveDot} aria-hidden="true" />В сети</dt>
            <dd><b>{value(live?.online)}</b><span className={styles.todayNote}>максимум сегодня {value(live?.peak_today)}</span></dd>
          </div>
          <div>
            <dt>Пользователей</dt>
            <dd><b>{fmtInt(totals.users)}</b><span className={styles.todayNote}>{live ? `+${fmtInt(live.new_today)} сегодня` : '— сегодня'}</span></dd>
          </div>
          <div>
            <dt>Серверов</dt>
            <dd><b>{fmtInt(totals.guilds)}</b><span className={styles.todayNote}>{live?.new_guilds_today !== undefined ? `+${fmtInt(live.new_guilds_today)} сегодня` : '— сегодня'}</span></dd>
          </div>
          <div>
            <dt>Бустов</dt>
            <dd><b>{fmtInt(totals.boosts)}</b><span className={styles.todayNote}>максимум сегодня {value(live?.peak_boosts_today)}</span></dd>
          </div>
        </dl>
        <dl className={styles.totals}>
          <div><dt>В голосе</dt><dd>{value(live?.in_voice)}</dd></div>
          <div><dt>Показывают экран</dt><dd>{value(live?.streaming)}</dd></div>
          <div><dt>С Плюсом</dt><dd>{fmtInt(totals.plus_active)}</dd></div>
          <div><dt>Ботов</dt><dd>{fmtInt(totals.bots)}</dd></div>
          <div><dt>Только с почтой</dt><dd>{fmtInt(totals.email_only)}</dd></div>
          <div className={totals.reports_open > 0 ? styles.attention : undefined}>
            <dt>{totals.reports_open > 0 ? <Flag size={12} aria-hidden="true" /> : null}Жалоб в очереди</dt>
            <dd>{fmtInt(totals.reports_open)}</dd>
          </div>
        </dl>
      </div>
    </section>
  )
}

/** Серверы с наибольшим числом сообщений за период. */
export function GuildsTable({ rows }: { rows: Dashboard['top_guilds'] }) {
  if (rows.length === 0) return <p className={styles.empty}>За период сообщений на серверах не было.</p>
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr><th>Сервер</th><th>Сообщений</th><th>Участников</th></tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.id}><td>{g.name}</td><td>{fmtInt(g.messages)}</td><td>{fmtInt(g.members)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Регистрации по помеченным ссылкам (?c=код) за период. */
export function SourcesTable({ rows }: { rows: Dashboard['sources'] }) {
  if (rows.length === 0) return <p className={styles.empty}>За период по помеченным ссылкам никто не зарегистрировался.</p>
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr><th>Кампания</th><th>Регистраций</th></tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.code}><td>{s.code}</td><td>{fmtInt(s.signups)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Все ряды по дням одной таблицей: то же, что на графиках, но числами. Новые дни сверху. */
export function DaysTable({ s }: { s: Dashboard['series'] }) {
  const order = s.days.map((_, i) => i).reverse()
  return (
    <details className={cx(styles.card, styles.details)}>
      <summary>Таблица по дням</summary>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>День</th><th>Аккаунтов</th><th>Удалено</th><th>Новые</th><th>Активные</th><th>Сообщ. на серверах</th><th>Сообщ. в личке</th>
              <th>Голос, ч</th><th>Трансляции, ч</th><th>Пик онлайна</th><th>Новых серверов</th><th>Заходы</th><th>Запросили код</th>
            </tr>
          </thead>
          <tbody>
            {order.map((i) => (
              <tr key={s.days[i]}>
                <td>{shortDay(s.days[i]!)}</td>
                <td>{fmtInt(s.accounts[i] ?? 0)}</td>
                <td>{fmtInt(s.deleted_users[i] ?? 0)}</td>
                <td>{fmtInt(s.new_users[i] ?? 0)}</td>
                <td>{fmtInt(s.active[i] ?? 0)}</td>
                <td>{fmtInt(s.messages_guild[i] ?? 0)}</td>
                <td>{fmtInt(s.messages_dm[i] ?? 0)}</td>
                <td>{fmtHours(s.voice_minutes[i] ?? 0)}</td>
                <td>{fmtHours(s.stream_minutes[i] ?? 0)}</td>
                <td>{fmtInt(s.peak_online[i] ?? 0)}</td>
                <td>{fmtInt(s.new_guilds[i] ?? 0)}</td>
                <td>{fmtInt(s.site_visits[i] ?? 0)}</td>
                <td>{fmtInt(s.signup_starts[i] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}
